import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'
import { apiRequest } from '@/lib/api'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Clock } from 'lucide-react'
// LegalPageHeader removed - now handled by PublicLegalLayout

interface LegalDocument {
  id: string
  type: string
  version: string
  locale: string
  content: string
  effective_at: string
}

// Map URL-friendly paths to actual document types in database
const URL_TO_DOCUMENT_TYPE: Record<string, string> = {
  'terms': 'platform_terms',
  'privacy': 'platform_privacy',
  'cookies': 'platform_cookies',
  'cookie_policy': 'platform_cookies',
  'terms_of_service': 'platform_terms',
  'privacy_policy': 'platform_privacy',
  'platform_terms': 'platform_terms',
  'platform_privacy': 'platform_privacy',
  'platform_cookies': 'platform_cookies',
  'business_terms': 'business_terms',
  'business_privacy': 'business_privacy',
  'agent_terms': 'agent_terms',
  'agent_privacy': 'agent_privacy',
}

/** Inject target="_blank" rel="noopener noreferrer" into all anchor tags */
function injectLinkTargets(html: string): string {
  return html.replace(/<a\b([^>]*)>/gi, (match, attrs: string) => {
    if (/\btarget\s*=/i.test(attrs)) return match
    const separator = attrs.trim().length > 0 && !attrs.endsWith(' ') ? ' ' : ''
    return `<a${attrs}${separator}target="_blank" rel="noopener noreferrer">`
  })
}

function LegalDocumentContent({ html }: { html: string }) {
  const processedHtml = useMemo(() => injectLinkTargets(html), [html])
  return (
    <div
      className="prose prose-sm max-w-none dark:prose-invert
        prose-headings:mt-8 prose-headings:mb-3
        prose-h2:text-xl prose-h2:font-semibold
        prose-p:my-0 prose-p:leading-7
        prose-ul:my-0 prose-ul:space-y-0
        prose-ol:my-0 prose-ol:space-y-0
        prose-li:my-0"
      dangerouslySetInnerHTML={{ __html: processedHtml }}
    />
  )
}

export function LegalPage() {
  const { type } = useParams<{ type: string }>()
  const [searchParams] = useSearchParams()
  const { t, i18n } = useTranslation()
  const locale = i18n.language
  const businessId = searchParams.get('business_id')

  const documentType = URL_TO_DOCUMENT_TYPE[type || ''] || 'platform_terms'

  const { data, isLoading, error: _error, isError } = useQuery({
    queryKey: ['legal-document', documentType, locale, businessId],
    queryFn: async () => {
      const params = new URLSearchParams({ locale })
      if (businessId) params.append('business_id', businessId)

      const response = await apiRequest<{ data: LegalDocument; inactive?: boolean }>(
        `/v1/legal-documents/${documentType}?${params.toString()}`
      )
      return response
    },
  })

  const isInactive = !isError && data?.inactive === true

  const getPageTitle = () => {
    switch (documentType) {
      case 'platform_terms':
        return t('legal.terms.title', 'Terms of Service')
      case 'platform_privacy':
        return t('legal.privacy.title', 'Privacy Policy')
      case 'platform_cookies':
        return t('legal.cookies.title', 'Cookie Policy')
      case 'business_terms':
        return t('legal.businessTerms.title', 'Business Terms & Conditions')
      case 'business_privacy':
        return t('legal.businessPrivacy.title', 'Business Privacy Policy')
      case 'agent_terms':
        return t('legal.agentTerms.title', 'Agent Terms of Service')
      case 'agent_privacy':
        return t('legal.agentPrivacy.title', 'Agent Privacy Policy')
      default:
        return t('legal.document.title', 'Legal Document')
    }
  }

  return (
    <div className="container mx-auto max-w-4xl px-4 py-12">
      <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-3xl font-semibold tracking-tight">{getPageTitle()}</CardTitle>
          </CardHeader>
          <CardContent className="pt-2 pb-10">
            {isLoading && (
              <div className="space-y-4">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-5/6" />
              </div>
            )}

            {isError && (
              <Alert variant="destructive">
                <AlertDescription>
                  {t('legal.error.loading', 'Failed to load document. Please try again later.')}
                </AlertDescription>
              </Alert>
            )}

            {isInactive && (
              <div className="flex flex-col items-center justify-center py-16 text-center space-y-4">
                <Clock className="h-12 w-12 text-muted-foreground" />
                <h2 className="text-xl font-semibold">
                  {t('legal.inactive.title', 'Document being updated')}
                </h2>
                <p className="text-muted-foreground max-w-md">
                  {t('legal.inactive.description', 'This legal document is currently being revised. Please check back later.')}
                </p>
              </div>
            )}

            {data?.data && (
              <div className="space-y-8">
                <div className="text-sm text-muted-foreground">
                  {t('legal.version', 'Version {{version}} — Effective {{date}}', {
                    version: data.data.version,
                    date: new Date(data.data.effective_at).toLocaleDateString(locale),
                  })}
                </div>
                <LegalDocumentContent html={data.data.content} />
              </div>
            )}

            {!isLoading && !isError && !isInactive && !data?.data && (
              <Alert>
                <AlertDescription>
                  {t('legal.notFound', 'Document not found.')}
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
      </Card>
    </div>
  )
}